import React from 'react';
import { useResponsive } from '../../hooks/useResponsive';
import { X, BarChart3, Activity, QrCode, Copy, ExternalLink, Clock } from 'lucide-react';

function StatsModal({ manager, onClose }) {
  const { isMobile } = useResponsive();
  const stats = {
    totalEvents: (manager.total_reviews || 0) * 3, // Simulated
    qrScans: manager.total_reviews || 0,
    urlCopies: Math.floor((manager.total_reviews || 0) * 0.5),
    redirects: manager.total_reviews || 0
  };

  // Simulated recent activity
  const recentActivity = [
    { type: 'scan', time: new Date().toLocaleString() },
    { type: 'copy', time: new Date(Date.now() - 120000).toLocaleString() },
    { type: 'redirect', time: new Date(Date.now() - 180000).toLocaleString() },
    { type: 'scan', time: new Date(Date.now() - 300000).toLocaleString() }
  ];

  const getActivityIcon = (type) => {
    const size = isMobile ? 18 : 20;
    switch(type) {
      case 'scan':
        return <QrCode size={size} color="#667eea" />;
      case 'copy':
        return <Copy size={size} color="#11998e" />;
      case 'redirect':
        return <ExternalLink size={size} color="#f093fb" />;
      default:
        return <Activity size={size} color="#7f8c8d" />;
    }
  };

  const getActivityLabel = (type) => {
    const labels = {
      scan: 'QR Code Scanned',
      copy: 'URL Copied',
      redirect: 'Redirected to Review'
    };
    return labels[type] || type;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: isMobile ? '16px' : '20px',
      overflowY: 'auto'
    }} onClick={onClose}>
      <div 
        className="card" 
        style={{ 
          width: '100%',
          maxWidth: isMobile ? '100%' : '700px',
          margin: isMobile ? '0' : 'auto',
          position: 'relative',
          background: 'white',
          padding: isMobile ? '20px' : '28px',
          maxHeight: isMobile ? '95vh' : 'auto',
          overflowY: isMobile ? 'auto' : 'visible'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: '12px',
          paddingBottom: '16px',
          borderBottom: '2px solid #e8ecf1',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '12px', flex: 1 }}>
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: isMobile ? '8px' : '10px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <BarChart3 size={isMobile ? 20 : 24} color="white" />
            </div>
            <h2 style={{ 
              margin: 0, 
              fontSize: isMobile ? '18px' : '24px', 
              fontWeight: '700', 
              color: '#2c3e50',
              wordBreak: 'break-word'
            }}>Analytics - {manager.manager_name}</h2>
          </div>
          <button 
            onClick={onClose}
            style={{
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              border: '2px solid #e8ecf1',
              borderRadius: '10px',
              cursor: 'pointer',
              color: '#495057',
              padding: isMobile ? '6px' : '8px',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)';
              e.currentTarget.style.color = '#c62828';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
              e.currentTarget.style.color = '#495057';
            }}
          >
            <X size={isMobile ? 18 : 20} />
          </button>
        </div>

        <p style={{ 
          color: '#7f8c8d', 
          marginBottom: isMobile ? '20px' : '28px', 
          fontSize: isMobile ? '14px' : '15px' 
        }}>
          Performance metrics and activity tracking
        </p>

        {/* Stats Grid */}
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(130px, 1fr))',
          gap: isMobile ? '12px' : '20px',
          marginBottom: isMobile ? '24px' : '32px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: isMobile ? '16px' : '24px',
            borderRadius: isMobile ? '12px' : '16px',
            textAlign: 'center',
            color: 'white',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
          }}>
            <Activity size={isMobile ? 24 : 28} style={{ marginBottom: isMobile ? '8px' : '12px', opacity: 0.9 }} />
            <div style={{ 
              fontSize: isMobile ? '28px' : '36px', 
              fontWeight: 'bold', 
              marginBottom: isMobile ? '6px' : '8px' 
            }}>
              {stats.totalEvents}
            </div>
            <div style={{ 
              fontSize: isMobile ? '12px' : '14px', 
              opacity: 0.9, 
              fontWeight: '600' 
            }}>Total Events</div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            padding: isMobile ? '16px' : '24px',
            borderRadius: isMobile ? '12px' : '16px',
            textAlign: 'center',
            color: 'white',
            boxShadow: '0 4px 12px rgba(240, 147, 251, 0.3)'
          }}>
            <QrCode size={isMobile ? 24 : 28} style={{ marginBottom: isMobile ? '8px' : '12px', opacity: 0.9 }} />
            <div style={{ 
              fontSize: isMobile ? '28px' : '36px', 
              fontWeight: 'bold', 
              marginBottom: isMobile ? '6px' : '8px' 
            }}>
              {stats.qrScans}
            </div>
            <div style={{ 
              fontSize: isMobile ? '12px' : '14px', 
              opacity: 0.9, 
              fontWeight: '600' 
            }}>QR Scans</div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            padding: isMobile ? '16px' : '24px',
            borderRadius: isMobile ? '12px' : '16px',
            textAlign: 'center',
            color: 'white',
            boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)'
          }}>
            <Copy size={isMobile ? 24 : 28} style={{ marginBottom: isMobile ? '8px' : '12px', opacity: 0.9 }} />
            <div style={{ 
              fontSize: isMobile ? '28px' : '36px', 
              fontWeight: 'bold', 
              marginBottom: isMobile ? '6px' : '8px' 
            }}>
              {stats.urlCopies}
            </div>
            <div style={{ 
              fontSize: isMobile ? '12px' : '14px', 
              opacity: 0.9, 
              fontWeight: '600' 
            }}>URL Copies</div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            padding: isMobile ? '16px' : '24px',
            borderRadius: isMobile ? '12px' : '16px',
            textAlign: 'center',
            color: 'white',
            boxShadow: '0 4px 12px rgba(79, 172, 254, 0.3)'
          }}>
            <ExternalLink size={isMobile ? 24 : 28} style={{ marginBottom: isMobile ? '8px' : '12px', opacity: 0.9 }} />
            <div style={{ 
              fontSize: isMobile ? '28px' : '36px', 
              fontWeight: 'bold', 
              marginBottom: isMobile ? '6px' : '8px' 
            }}>
              {stats.redirects}
            </div>
            <div style={{ 
              fontSize: isMobile ? '12px' : '14px', 
              opacity: 0.9, 
              fontWeight: '600' 
            }}>Redirects</div>
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px', 
            marginBottom: '16px' 
          }}>
            <Clock size={isMobile ? 18 : 20} color="#667eea" />
            <h3 style={{ 
              margin: 0, 
              fontSize: isMobile ? '16px' : '18px', 
              fontWeight: '600', 
              color: '#2c3e50' 
            }}>Recent Activity</h3>
          </div>
          <div style={{ 
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            borderRadius: isMobile ? '12px' : '16px',
            overflow: 'hidden',
            border: '1px solid #e8ecf1'
          }}>
            {recentActivity.map((activity, index) => (
              <div 
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: isMobile ? '14px 16px' : '18px 24px',
                  borderBottom: index < recentActivity.length - 1 ? '1px solid #e8ecf1' : 'none',
                  background: 'white',
                  transition: 'all 0.3s',
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                  gap: isMobile ? '8px' : '0'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
                  e.currentTarget.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px' }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    padding: isMobile ? '8px' : '10px',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {getActivityIcon(activity.type)}
                  </div>
                  <span style={{ 
                    fontWeight: '600', 
                    color: '#2c3e50', 
                    fontSize: isMobile ? '14px' : '15px' 
                  }}>{getActivityLabel(activity.type)}</span>
                </div>
                <span style={{ 
                  color: '#7f8c8d', 
                  fontSize: isMobile ? '12px' : '14px', 
                  fontWeight: '500',
                  width: isMobile ? '100%' : 'auto',
                  textAlign: isMobile ? 'left' : 'right',
                  paddingLeft: isMobile ? '40px' : '0'
                }}>{activity.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatsModal;

//     totalEvents: (manager.total_reviews || 0) * 3, // Simulated
//     qrScans: manager.total_reviews || 0,
//     urlCopies: Math.floor((manager.total_reviews || 0) * 0.5),
//     redirects: manager.total_reviews || 0
//   };

//   // Simulated recent activity
//   const recentActivity = [
//     { type: 'scan', time: new Date().toLocaleString() },
//     { type: 'copy', time: new Date(Date.now() - 120000).toLocaleString() },
//     { type: 'redirect', time: new Date(Date.now() - 180000).toLocaleString() },
//     { type: 'scan', time: new Date(Date.now() - 300000).toLocaleString() }
//   ];

//   const getActivityIcon = (type) => {
//     switch(type) {
//       case 'scan':
//         return <QrCode size={20} color="#667eea" />;
//       case 'copy':
//         return <Copy size={20} color="#11998e" />;
//       case 'redirect':
//         return <ExternalLink size={20} color="#f093fb" />;
//       default:
//         return <Activity size={20} color="#7f8c8d" />;
//     }
//   };

//   const getActivityLabel = (type) => {
//     const labels = {
//       scan: 'QR Code Scanned',
//       copy: 'URL Copied',
//       redirect: 'Redirected to Review'
//     };
//     return labels[type] || type;
//   };

//   return (
//     <div style={{
//       position: 'fixed',
//       top: 0,
//       left: 0,
//       right: 0,
//       bottom: 0,
//       background: 'rgba(0,0,0,0.7)',
//       display: 'flex',
//       alignItems: 'center',
//       justifyContent: 'center',
//       zIndex: 10000,
//       padding: '20px'
//     }} onClick={onClose}>
//       <div 
//         className="card" 
//         style={{ 
//           width: '100%',
//           maxWidth: '700px',
//           margin: '0',
//           position: 'relative',
//           background: 'white'
//         }}
//         onClick={(e) => e.stopPropagation()}
//       >
//         {/* Header */}
//         <div style={{ 
//           display: 'flex', 
//           alignItems: 'center', 
//           justifyContent: 'space-between',
//           marginBottom: '12px',
//           paddingBottom: '16px',
//           borderBottom: '2px solid #e8ecf1'
//         }}>
//           <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
//             <div style={{
//               background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
//               padding: '10px',
//               borderRadius: '10px',
//               display: 'flex',
//               alignItems: 'center',
//               justifyContent: 'center'
//             }}>
//               <BarChart3 size={24} color="white" />
//             </div>
//             <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#2c3e50' }}>Analytics - {manager.manager_name}</h2>
//           </div>
//           <button 
//             onClick={onClose}
//             style={{
//               background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
//               border: '2px solid #e8ecf1',
//               borderRadius: '10px',
//               cursor: 'pointer',
//               color: '#495057',
//               padding: '8px',
//               display: 'flex',
//               alignItems: 'center',
//               transition: 'all 0.3s'
//             }}
//             onMouseEnter={(e) => {
//               e.currentTarget.style.background = 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)';
//               e.currentTarget.style.color = '#c62828';
//             }}
//             onMouseLeave={(e) => {
//               e.currentTarget.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
//               e.currentTarget.style.color = '#495057';
//             }}
//           >
//             <X size={20} />
//           </button>
//         </div>

//         <p style={{ color: '#7f8c8d', marginBottom: '28px', fontSize: '15px' }}>
//           Performance metrics and activity tracking
//         </p>

//         {/* Stats Grid */}
//         <div style={{ 
//           display: 'grid',
//           gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
//           gap: '20px',
//           marginBottom: '32px'
//         }}>
//           <div style={{
//             background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
//             padding: '24px',
//             borderRadius: '16px',
//             textAlign: 'center',
//             color: 'white',
//             boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
//           }}>
//             <Activity size={28} style={{ marginBottom: '12px', opacity: 0.9 }} />
//             <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
//               {stats.totalEvents}
//             </div>
//             <div style={{ fontSize: '14px', opacity: 0.9, fontWeight: '600' }}>Total Events</div>
//           </div>

//           <div style={{
//             background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
//             padding: '24px',
//             borderRadius: '16px',
//             textAlign: 'center',
//             color: 'white',
//             boxShadow: '0 4px 12px rgba(240, 147, 251, 0.3)'
//           }}>
//             <QrCode size={28} style={{ marginBottom: '12px', opacity: 0.9 }} />
//             <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
//               {stats.qrScans}
//             </div>
//             <div style={{ fontSize: '14px', opacity: 0.9, fontWeight: '600' }}>QR Scans</div>
//           </div>

//           <div style={{
//             background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
//             padding: '24px',
//             borderRadius: '16px',
//             textAlign: 'center',
//             color: 'white',
//             boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)'
//           }}>
//             <Copy size={28} style={{ marginBottom: '12px', opacity: 0.9 }} />
//             <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
//               {stats.urlCopies}
//             </div>
//             <div style={{ fontSize: '14px', opacity: 0.9, fontWeight: '600' }}>URL Copies</div>
//           </div>

//           <div style={{
//             background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
//             padding: '24px',
//             borderRadius: '16px',
//             textAlign: 'center',
//             color: 'white',
//             boxShadow: '0 4px 12px rgba(79, 172, 254, 0.3)'
//           }}>
//             <ExternalLink size={28} style={{ marginBottom: '12px', opacity: 0.9 }} />
//             <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
//               {stats.redirects}
//             </div>
//             <div style={{ fontSize: '14px', opacity: 0.9, fontWeight: '600' }}>Redirects</div>
//           </div>
//         </div>

//         {/* Recent Activity */}
//         <div>
//           <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
//             <Clock size={20} color="#667eea" />
//             <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>Recent Activity</h3>
//           </div>
//           <div style={{ 
//             background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
//             borderRadius: '16px',
//             overflow: 'hidden',
//             border: '1px solid #e8ecf1'
//           }}>
//             {recentActivity.map((activity, index) => (
//               <div 
//                 key={index}
//                 style={{
//                   display: 'flex',
//                   alignItems: 'center',
//                   justifyContent: 'space-between',
//                   padding: '18px 24px',
//                   borderBottom: index < recentActivity.length - 1 ? '1px solid #e8ecf1' : 'none',
//                   background: 'white',
//                   transition: 'all 0.3s'
//                 }}
//                 onMouseEnter={(e) => {
//                   e.currentTarget.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
//                   e.currentTarget.style.transform = 'translateX(4px)';
//                 }}
//                 onMouseLeave={(e) => {
//                   e.currentTarget.style.background = 'white';
//                   e.currentTarget.style.transform = 'translateX(0)';
//                 }}
//               >
//                 <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
//                   <div style={{
//                     background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
//                     padding: '10px',
//                     borderRadius: '10px',
//                     display: 'flex',
//                     alignItems: 'center',
//                     justifyContent: 'center'
//                   }}>
//                     {getActivityIcon(activity.type)}
//                   </div>
//                   <span style={{ fontWeight: '600', color: '#2c3e50', fontSize: '15px' }}>{getActivityLabel(activity.type)}</span>
//                 </div>
//                 <span style={{ color: '#7f8c8d', fontSize: '14px', fontWeight: '500' }}>{activity.time}</span>
//               </div>
//             ))}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default StatsModal;
